"use client";
import { loadSvgFromUrl } from "@/utils/svgUtils";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Vivus from "vivus";

const Vine: React.FC = () => {
    const [svgContent, setSvgContent] = useState<string>("");
    const [isDesktop, setIsDesktop] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Declare flowerIds and sectionIds so they’re available in all effects
    const flowerIds = useMemo(
        () =>
            new Set([
                "path162",
                "path159",
                "path160",
                "path158",
                "path164",
                "path161",
                "path157",
                "path163",
                "path156",
            ]),
        []
    );

    const sectionIds = useMemo(
        () => [
            "projects-section",
            "about-me",
            "contracts-section",
            "contact-section",
            "academia-section",
        ],
        []
    );

    // Check if screen is desktop size
    useEffect(() => {
        const checkScreenSize = () => {
            setIsDesktop(window.innerWidth >= 1910);
        };
        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);
        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);

    // Load SVG only on desktop
    useEffect(() => {
        if (isDesktop) {
            loadSvgFromUrl("/images/groupedVine.svg")
                .then((content) => {
                    setSvgContent(content);
                    console.log("SVG loaded:", content.slice(0, 100) + "...");
                })
                .catch((error) => console.error("Error loading SVG:", error));
        } else {
            setSvgContent("");
        }
    }, [isDesktop]);

    // Make SVG responsive
    useEffect(() => {
        if (!containerRef.current || !svgContent) return;
        const container = containerRef.current;
        setTimeout(() => {
            const svgElement = container.querySelector("svg");
            if (svgElement instanceof SVGElement) {
                svgElement.style.width = "100%";
                svgElement.style.height = `${window.innerHeight}px`;
                svgElement.setAttribute("preserveAspectRatio", "xMidYMid meet");
                svgElement.style.transform = "translateX(-25%)";
            }
        }, 10);
    }, [svgContent]);

    // Function that updates the vine colors based on mapped sections
    const updateVineColors = useCallback(() => {
        if (!containerRef.current || !svgContent) return;
        const container = containerRef.current;
        const svgElement = container.querySelector("svg");
        if (!svgElement) return;

        // Check if any mapped section is visible in the viewport.
        const visibleSections = sectionIds
            .map((id) => document.getElementById(id))
            .filter(
                (el): el is HTMLElement =>
                    el !== null &&
                    el.getBoundingClientRect().bottom > 0 &&
                    el.getBoundingClientRect().top < window.innerHeight
            );
        if (visibleSections.length === 0) {
            container.style.display = "none";
            return;
        } else {
            container.style.display = "block";
        }

        // Helper: Interpolate between two RGB colors.
        const interpolateColor = (color1: number[], color2: number[], t: number): string => {
            const r = Math.round((1 - t) * color1[0] + t * color2[0]);
            const g = Math.round((1 - t) * color1[1] + t * color2[1]);
            const b = Math.round((1 - t) * color1[2] + t * color2[2]);
            return `rgb(${r}, ${g}, ${b})`;
        };

        // Build keypoints from the mapped sections.
        // Each keypoint: position (y, clamped to [0, window.innerHeight]) and color ([255,255,255] for dark, [0,0,0] for light).
        const keypoints: { pos: number; color: number[] }[] = [];
        sectionIds.forEach((id) => {
            const section = document.getElementById(id);
            if (section) {
                const rect = section.getBoundingClientRect();
                const centerY = rect.top + rect.height / 2;
                const clampedY = Math.max(0, Math.min(centerY, window.innerHeight));
                const theme = section.getAttribute("data-theme");
                const col = theme === "dark" ? [255, 255, 255] : [0, 0, 0];
                keypoints.push({ pos: clampedY, color: col });
            }
        });

        // Only use the mapped keypoints (no explicit top keypoint).
        if (keypoints.length === 0) {
            container.style.display = "none";
            return;
        }
        // Add a bottom boundary keypoint using the lowest mapped section's color.
        keypoints.push({
            pos: window.innerHeight,
            color: keypoints.reduce((prev, cur) => (prev.pos > cur.pos ? prev : cur)).color,
        });
        keypoints.sort((a, b) => a.pos - b.pos);

        // For each SVG path, determine its vertical midpoint and interpolate its color.
        const allPaths = svgElement.querySelectorAll("path");
        allPaths.forEach((path) => {
            let rect;
            try {
                rect = path.getBoundingClientRect();
            } catch (e) {
                return;
            }
            const midY = rect.top + rect.height / 2;
            // Find the two keypoints that bracket midY.
            let lower = keypoints[0],
                upper = keypoints[keypoints.length - 1];
            for (let i = 0; i < keypoints.length - 1; i++) {
                if (midY >= keypoints[i].pos && midY <= keypoints[i + 1].pos) {
                    lower = keypoints[i];
                    upper = keypoints[i + 1];
                    break;
                }
            }
            let t = 0;
            if (upper.pos !== lower.pos) {
                t = (midY - lower.pos) / (upper.pos - lower.pos);
            }
            const interpolatedColor = interpolateColor(lower.color, upper.color, t);

            if (path.id && flowerIds.has(path.id)) {
                path.setAttribute("fill", interpolatedColor);
                path.setAttribute("stroke", interpolatedColor);
                (path as SVGElement).style.fill = interpolatedColor;
                (path as SVGElement).style.stroke = interpolatedColor;
            } else {
                path.setAttribute("fill", "transparent");
                path.setAttribute("stroke", interpolatedColor);
                (path as SVGElement).style.fill = "transparent";
                (path as SVGElement).style.stroke = interpolatedColor;
            }
            // Preserve stroke settings.
            path.setAttribute("stroke-width", "0.1");
            path.setAttribute("stroke-opacity", "1");
            path.setAttribute("fill-opacity", "1");
            (path as SVGElement).style.strokeWidth = "0.1px";
            (path as SVGElement).style.opacity = "1";
        });
    }, [svgContent, sectionIds, flowerIds]);

    // Call updateVineColors on mount (with a delay) and on scroll/resize events.
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            updateVineColors();
        }, 500);
        window.addEventListener("scroll", updateVineColors);
        window.addEventListener("resize", updateVineColors);
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener("scroll", updateVineColors);
            window.removeEventListener("resize", updateVineColors);
        };
    }, [updateVineColors]);

    // Initialize Vivus animation (if needed)
    useEffect(() => {
        if (!containerRef.current || !svgContent) return;
        const svgElement = containerRef.current.querySelector("svg");
        if (!svgElement) return;
        try {
            if (typeof Vivus !== "undefined") {
                new Vivus(svgElement as unknown as HTMLElement, {
                    type: "sync",
                    duration: 200,
                    start: "autostart",
                    animTimingFunction: (x: number) => x * x,
                });
            }
        } catch (error) {
            console.error("Error initializing Vivus:", error);
        }
    }, [svgContent]);

    if (!isDesktop) return null;
    return (
        <div
            ref={containerRef}
            className="fixed left-0 top-0 z-10 pointer-events-none hidden lg:block"
            style={{ filter: "drop-shadow(0 0 10px rgba(0, 0, 0, 0.3))" }}
            dangerouslySetInnerHTML={{ __html: svgContent }}
        />
    );
};

export default Vine;
